import React from "react";
import {
  UsersRound,
  BriefcaseBusiness,
  ShieldCheck,
  GraduationCap,
  Plane,
  Building2,
  Globe,
  BarChart3,
  BadgeCheck,
  Users,
} from "lucide-react";
import {
  immigrantVisa,
  indHeroTwo,
  visaGlobalMobility,
} from "../../../../../images";

interface HighlightItem {
  icon: JSX.Element;
  text: string;
}

interface VisaCategory {
  headline: JSX.Element | string;
  description: string;
  destination: string;
  highlights: HighlightItem[];
  imageSrc: string;
  imageAlt: string;
  layoutVariant: "default" | "reversed";
  fromColor?: string;
  toColor?: string;
}

const CATEGORIES: VisaCategory[] = [
  {
    destination: "immigrant visas",
    headline: (
      <span>
        <span className="text-teal-00 italic mx-1">Immigrant</span>{" "}
        Visas
        <br />
        <span className="text-gray-400 font-light text-2xl">
          Permanent pathways, expertly guided
        </span>
      </span>
    ),
    description:
      "Whether you are reuniting with family, advancing your career abroad, or pursuing long-term residency, SeamlessVisa provides end-to-end support for permanent immigration pathways.",
    highlights: [
      { icon: <UsersRound />, text: "Family sponsorship petitions" },
      {
        icon: <BriefcaseBusiness />,
        text: "Skilled worker immigration pathways",
      },
      { icon: <BadgeCheck />, text: "Permanent residency programs" },
      { icon: <Globe />, text: "Express Entry applications" },
      { icon: <ShieldCheck />, text: "Long-term residency planning" },
    ],
    imageSrc: immigrantVisa,
    imageAlt: "Immigrant visa consultation with an expert",
    layoutVariant: "default",
    fromColor: "from-teal-00/30!",
    toColor: "to-[#f0faf8]!",
  },
  {
    destination: "non-immigrant visas",
    headline: (
      <span>
        <span className="text-blue-00 italic mx-1">Non-Immigrant</span>{" "}
        Visas
        <br />
        <span className="text-gray-400 font-light text-2xl">
          Temporary travel, zero complexity
        </span>
      </span>
    ),
    description:
      "For those traveling temporarily, we make the visa process smooth and predictable. From student and visitor visas to business travel and dependent visas, we handle the complexity so you can focus on your journey.",
    highlights: [
      {
        icon: <GraduationCap />,
        text: "Student visas (F-1, M-1, J-1 and equivalents)",
      },
      { icon: <Plane />, text: "Visitor and tourism visas (B-1/B-2)" },
      { icon: <BriefcaseBusiness />, text: "Business travel visas" },
      { icon: <UsersRound />, text: "Dependent and accompanying family visas" },
      { icon: <BadgeCheck />, text: "Work authorisation and H-1B support" },
    ],
    imageSrc: indHeroTwo,
    imageAlt: "Student reviewing visa documents for study abroad",
    layoutVariant: "reversed",
    fromColor: "from-blue-00/20!",
    toColor: "to-blue-ea!",
  },
  {
    destination: "global mobility",
    headline: (
      <span>
        <span className="text-blue-00 italic mx-1">Global Mobility</span>{" "}
        Solutions
        <br />
        <span className="text-gray-400 font-light text-2xl">
          For organisations moving people at scale
        </span>
      </span>
    ),
    description:
      "International mobility requires precision, coordination, and trusted expertise. We partner directly with businesses and service providers to manage visa applications at scale — handling bulk processing, compliance, and staff relocations with the rigour your organisation needs.",
    highlights: [
      { icon: <Building2 />, text: "Corporate relocation programs" },
      { icon: <Users />, text: "Bulk H-2B and seasonal worker processing" },
      {
        icon: <BarChart3 />,
        text: "Staff immigration strategy and compliance",
      },
      { icon: <ShieldCheck />, text: "Dedicated account management" },
      {
        icon: <BadgeCheck />,
        text: "Volume discounts for frequent applicants",
      },
    ],
    imageSrc: visaGlobalMobility,
    imageAlt: "Business team reviewing corporate relocation visa strategy",
    layoutVariant: "default",
    fromColor: "from-blue-ea!",
    toColor: "to-white!",
  },
];

const VisaCategories = () => {
  return (
    <section id="visa-categories" className="py-20 px-4 md:px-8 bg-white">
      {/* Section header — matches Destinations.tsx header pattern exactly */}
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <h2 className="text-black-1a text-4xl md:text-5xl font-light tracking-tight mb-6">
          Visa Pathways for{" "}
          <span className="text-teal-00 italic mx-1">Every Journey</span>
        </h2>
        <p className="text-dark-6b text-base">
          Whether you're moving permanently, traveling temporarily, or
          relocating a team, we have the expertise to support you.
        </p>
      </div>

      {/* Category sections — matches DestinationSection structure exactly */}
      {CATEGORIES.map((category, i) => (
        <div key={i} className="bg-white my-12">
          <div className="px-6 py-16">
            <div
              className={`md:flex gap-8 ${category.layoutVariant === "reversed"
                ? "flex-row-reverse"
                : "flex-row"
                } items-center max-w-7xl mx-auto`}
            >
              {/* Content — matches DestinationContent exactly */}
              <div className="flex-1 pr-8">
                <div className="mb-6">
                  <h4 className="text-3xl font-light text-gray-900 leading-tight mb-4">
                    {category.headline}
                  </h4>
                </div>
                <div className="mb-8">
                  <p className="text-gray-600 text-base leading-relaxed">
                    {category.description}
                  </p>
                </div>
                <div className="mb-6">
                  <p className="uppercase font-light text-black-31 mb-4">
                    Why choose <span>{category.destination}</span>?
                  </p>
                </div>
                <div>
                  {category.highlights.map((feature, j) => (
                    /* DestinationFeatureItem style */
                    <div key={j} className="flex flex-row items-center mb-4">
                      <div className="w-1 h-8 mr-3 bg-grey-a7 rounded-full" />
                      <div className="w-6 h-6 mr-3">{feature.icon}</div>
                      <div className="flex-1">
                        <p className="text-gray-700 text-base leading-relaxed">
                          {feature.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image — matches DestinationImage exactly */}
              <div
                className={`h-180 rounded-4xl bg-linear-to-t ${category.fromColor} ${category.toColor} flex-1 overflow-hidden flex items-center ${category.layoutVariant === "reversed"
                  ? "justify-start"
                  : "justify-end"
                  }`}
              >
                <div className="w-[90%] h-[90%]">
                  <img
                    src={category.imageSrc}
                    alt={category.imageAlt}
                    className={`w-full h-full rounded-2xl object-cover ${category.layoutVariant === "reversed"
                      ? "rounded-l-none"
                      : "rounded-r-none"
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default VisaCategories;
